import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StatusFilterTabs from '../../components/shared/StatusFilterTabs';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from '../../components/shared/GridColumnFilterPanel';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import { Button } from '../../components/ui/button';
import { DataGrid, type GridApi } from '../../components/ui/table';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { showToast } from '../../store/toastStore';
import { approveStorefrontUrl, fetchStorefrontUrlReviewGrid, rejectStorefrontUrl } from './api/api';
import {
  buildStorefrontUrlReviewColumnFilterFields,
  createStorefrontUrlReviewColumnDefs,
  STOREFRONT_URL_REVIEW_COLUMN_LABELS,
} from './table/columnDefs';
import {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  STOREFRONT_URL_REVIEW_GRID_DEFAULT_COL_DEF,
  storefrontUrlReviewGridOptions,
} from './table/gridOptions';
import type {
  StorefrontUrlReviewColumnFilterKey,
  StorefrontUrlReviewColumnFilters,
  StorefrontUrlReviewContext,
} from './models/types';
import type { StorefrontUrlSubmissionRow } from '../storefront-urls/models/types';
import { getApiErrorMessage } from '../storefront-urls/utils/utils';
import { EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS, SEARCH_DEBOUNCE_MS } from './constants/constants';
import {
  buildStorefrontUrlReviewRequest,
  filterStorefrontUrlReviewRows,
  mapAgGridFilterModelToStorefrontUrlReviewFilters,
} from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import RejectUrlDialog from './RejectUrlDialog';
import '../merchants/Merchants.css';
import { BsDownload, BsUpload } from 'react-icons/bs';
import { useAuthStore } from '../../store/authStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  bulkUploadStorefrontUrls,
  downloadStorefrontUrlTemplate,
  type BulkUploadResult,
} from './api/api';

const STATUS_TABS = ['All', 'Pending', 'Approved', 'Rejected'] as const;

const StorefrontUrlReview: React.FC = () => {
  const [rows, setRows] = useState<StorefrontUrlSubmissionRow[]>([]);
  const [statsRows, setStatsRows] = useState<StorefrontUrlSubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<string>('Pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<StorefrontUrlReviewColumnFilters>(
    EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const gridApiRef = useRef<GridApi<StorefrontUrlSubmissionRow> | null>(null);
  const tableFiltersRef = useRef({
    filterStatus: statusTab,
    search: debouncedSearch,
    columnFilters,
  });

  const [rejectTarget, setRejectTarget] = useState<StorefrontUrlSubmissionRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Super-admin bulk upload of storefront URLs.
  const authUser = useAuthStore((state) => state.user);
  const isSuperAdmin =
    String(authUser?.adminType ?? authUser?.role ?? '').trim().toUpperCase() === 'SUPER_ADMIN';
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleStatusTabChange = useCallback((value: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      filterStatus: value,
    };
    setStatusTab(value);
  }, []);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    const applyDrawerState = () => {
      setColumnFilters((prev) => ({
        ...prev,
        [key]: null,
      }));
    };
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      // Clear the header filter first so loadRows' native-wins merge does not
      // re-apply the value we just removed, then refetch.
      void api.setColumnFilterModel(key, null).then(() => {
        applyDrawerState();
        api.onFilterChanged();
      });
    } else {
      applyDrawerState();
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback((values: Record<string, string[] | null>) => {
    const nextColumnFilters = {
      ...EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS,
      ...(Object.fromEntries(
        (Object.keys(EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS) as StorefrontUrlReviewColumnFilterKey[]).map(
          (key) => [key, values[key]?.length ? values[key] : null],
        ),
      ) as StorefrontUrlReviewColumnFilters),
    };
    // Update the ref synchronously so the refetch (from columnFilters or a native
    // setFilterModel(null) → onFilterChanged) already sends the drawer values.
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: nextColumnFilters,
    };
    // Drawer is the source of truth after Apply: clear lingering header-funnel filters
    // so they can't override the drawer selection in loadRows' merge (native wins).
    gridApiRef.current?.setFilterModel(null);
    setColumnFilters(nextColumnFilters);
  }, []);

  const handleGridFilterChanged = useCallback(() => {
    if (!hasLoadedOnceRef.current) return;
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<StorefrontUrlSubmissionRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const mergedColumnFilters = useMemo<StorefrontUrlReviewColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToStorefrontUrlReviewFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const loadReviewData = useCallback(async (
    isActive?: () => boolean,
    options?: { refreshStats?: boolean },
  ) => {
    const isInitialLoad = !hasLoadedOnceRef.current;
    const refreshStats = Boolean(options?.refreshStats) || isInitialLoad;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefetching(true);
    }
    setLoadError(null);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToStorefrontUrlReviewFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      const mergedColumnFilters: StorefrontUrlReviewColumnFilters = {
        ...tableFiltersRef.current.columnFilters,
        ...nativeColumnFilters,
      };
      const filterOptions = {
        filterStatus: tableFiltersRef.current.filterStatus,
        search: tableFiltersRef.current.search,
        columnFilters: mergedColumnFilters,
      };

      if (refreshStats) {
        const { rows: allRows } = await fetchStorefrontUrlReviewGrid({
          filterStatus: 'All',
          search: '',
          columnFilters: EMPTY_STOREFRONT_URL_REVIEW_COLUMN_FILTERS,
        });
        if (isActive && !isActive()) return;
        setStatsRows(allRows);
        setRows(filterStorefrontUrlReviewRows(allRows, filterOptions));
      } else {
        const { rows: nextRows } = await fetchStorefrontUrlReviewGrid(filterOptions);
        if (isActive && !isActive()) return;
        setRows(nextRows);
      }
    } catch (err: unknown) {
      if (isActive && !isActive()) return;
      setLoadError(getApiErrorMessage(err, 'Failed to load URL submissions.'));
      setRows([]);
      if (refreshStats) setStatsRows([]);
    } finally {
      if (!isActive || isActive()) {
        setLoading(false);
        setIsRefetching(false);
        hasLoadedOnceRef.current = true;
      }
    }
  }, [statusTab, debouncedSearch, columnFilters, gridFilterEpoch]);

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus: statusTab,
      search: debouncedSearch,
      columnFilters,
    };
  }, [statusTab, debouncedSearch, columnFilters]);

  const refreshRows = useCallback(async () => {
    await loadReviewData(undefined, { refreshStats: true });
  }, [loadReviewData]);

  useAsyncEffect(async (isActive) => {
    await loadReviewData(isActive);
  }, [loadReviewData]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [StorefrontUrlReviewColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: STOREFRONT_URL_REVIEW_COLUMN_LABELS[key],
        value:
          key === 'createdAt'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const fields = buildStorefrontUrlReviewColumnFilterFields(
      statsRows.length > 0 ? statsRows : rows,
    );
    return fields.map((field) => {
      if (field.type === 'dateRange') return field;
      const applied = mergedColumnFilters[field.key as StorefrontUrlReviewColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [rows, statsRows, mergedColumnFilters]);

  useEffect(() => {
    gridApiRef.current?.paginationGoToFirstPage();
  }, [statusTab, debouncedSearch, columnFilters, gridFilterEpoch]);

  const getTabCount = useCallback(
    (tab: string | { value: string; label: string }) => {
      const value = typeof tab === 'string' ? tab : tab.value;
      return value === 'All'
        ? statsRows.length
        : statsRows.filter((row) => row.status.toLowerCase() === value.toLowerCase()).length;
    },
    [statsRows],
  );

  const handleApprove = async (row: StorefrontUrlSubmissionRow) => {
    try {
      await approveStorefrontUrl(row.id);
      showToast('URL approved.', 'success');
      await refreshRows();
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, 'Failed to approve URL.'), 'error');
    }
  };

  const openReject = (row: StorefrontUrlSubmissionRow) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectError(null);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    setRejectError(null);
    try {
      await rejectStorefrontUrl(rejectTarget.id, rejectReason.trim());
      showToast('URL rejected.', 'success');
      setRejectTarget(null);
      await refreshRows();
    } catch (err: unknown) {
      setRejectError(getApiErrorMessage(err, 'Failed to reject URL.'));
    } finally {
      setRejecting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToStorefrontUrlReviewFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      await downloadExcelExport({
        type: EXPORT_TYPES.STOREFRONT_URLS,
        fileNamePrefix: 'url-approvals',
        errorMessage: 'Failed to export URL submissions.',
        payload: buildStorefrontUrlReviewRequest({
          filterStatus: statusTab,
          search: debouncedSearch,
          columnFilters: {
            ...columnFilters,
            ...nativeColumnFilters,
          },
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadStorefrontUrlTemplate();
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Failed to download template.'), 'error');
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-selecting the SAME file still fires onChange.
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const result = await bulkUploadStorefrontUrls(file);
      await refreshRows();
      if (result.failures.length > 0) {
        setUploadResult(result); // show the per-row failures dialog
        showToast(result.message ?? 'Upload completed with some errors.', 'info');
      } else {
        showToast(
          result.message ?? `Uploaded ${result.created} URL(s).`,
          'success',
        );
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Failed to upload file.'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const columnDefs = useMemo(() => createStorefrontUrlReviewColumnDefs(), []);
  const gridContext = useMemo<StorefrontUrlReviewContext>(
    () => ({ onApprove: handleApprove, onReject: openReject }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const gridOptions = useMemo(
    () => ({
      ...storefrontUrlReviewGridOptions,
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleGridFilterChanged],
  );

  const gridLoading = loading || isRefetching;

  return (
    <ListConsolePage
      title="URL Approvals"
      error={
        loadError ? (
          <span className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>{loadError}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tm-console-action-btn tm-console-action-btn--outline w-fit shrink-0"
              onClick={() => void refreshRows()}
            >
              Retry
            </Button>
          </span>
        ) : null
      }
    >
      <ListConsoleGridSection
        toolbarLeft={
          <StatusFilterTabs
            tabs={STATUS_TABS}
            value={statusTab}
            onChange={handleStatusTabChange}
            getCount={getTabCount}
          />
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search merchant, URL, status…"
              disabled={gridLoading}
              className="tm-toolbar-search"
              aria-label="Search submissions"
            />
            <GridColumnFilterButton
              activeCount={activeColumnFilters.length}
              onClick={() => setFilterPanelOpen(true)}
              disabled={gridLoading}
              className="tm-compact-icon-btn"
            />
            {isSuperAdmin && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleFileSelected}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tm-console-action-btn tm-console-action-btn--outline gap-1.5"
                  onClick={() => void handleDownloadTemplate()}
                >
                  <BsDownload className="h-3 w-3 shrink-0" aria-hidden /> Template
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="tm-console-action-btn gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <BsUpload className="h-3 w-3 shrink-0" aria-hidden />
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </>
            )}
            <GridExportButton
              onClick={handleExport}
              loading={exporting}
              disabled={gridLoading || rows.length === 0}
              className="tm-compact-icon-btn"
            />
          </div>
        }
        filterBar={
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
        }
      >
        <DataGrid<StorefrontUrlSubmissionRow>
          rowData={rows}
          columnDefs={columnDefs}
          context={gridContext}
          loading={gridLoading}
          pagination
          paginationPageSize={GRID_PAGE_SIZE}
          paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
          height="100%"
          className={LIST_CONSOLE_AG_GRID_CLASS}
          onGridReady={handleGridReady}
          defaultColDef={STOREFRONT_URL_REVIEW_GRID_DEFAULT_COL_DEF}
          gridOptions={gridOptions}
        />
      </ListConsoleGridSection>

      <RejectUrlDialog
        open={!!rejectTarget}
        merchantName={rejectTarget?.merchantName ?? ''}
        url={rejectTarget?.url ?? ''}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        loading={rejecting}
        error={rejectError}
        onConfirm={confirmReject}
        onCancel={() => !rejecting && setRejectTarget(null)}
      />

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />

      {/* Bulk-upload result — shown only when some rows failed, so the admin can fix and re-upload. */}
      <Dialog open={!!uploadResult} onOpenChange={(open) => !open && setUploadResult(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload result</DialogTitle>
          </DialogHeader>
          {uploadResult && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="text-muted-foreground">Rows: <b className="text-foreground">{uploadResult.totalRows}</b></span>
                <span className="text-green-600">Created: <b>{uploadResult.created}</b></span>
                <span className="text-amber-600">Duplicates skipped: <b>{uploadResult.skipped}</b></span>
                <span className="text-destructive">Failed: <b>{uploadResult.failures.length}</b></span>
              </div>
              {uploadResult.failures.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="p-2 text-left font-medium">Row</th>
                        <th className="p-2 text-left font-medium">Merchant</th>
                        <th className="p-2 text-left font-medium">URL</th>
                        <th className="p-2 text-left font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.failures.map((f, i) => (
                        <tr key={i} className="border-b border-border/60 last:border-0">
                          <td className="p-2 text-muted-foreground">{f.row}</td>
                          <td className="p-2">{f.merchantName || '—'}</td>
                          <td className="p-2 break-all text-muted-foreground">{f.url || '—'}</td>
                          <td className="p-2 text-destructive">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ListConsolePage>
  );
};

export default StorefrontUrlReview;
